import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { CallIntelChip } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { useCallIntelStatsComparisonData } from '../../hooks/useCallIntelStatsComparisonData';
import { PercentChangeIndicator } from '../percent-change-indicator';

dayjs.extend(utc);

export const SummaryPanel: FC<{ locationIds: string[] }> = ({ locationIds = [] }) => {
  const { t } = useTranslation('callIntelDashboardWidgets');
  const { currentOverviewDetails, previousOverviewDetails, currentStats, previousStats } =
    useCallIntelStatsComparisonData({
      locationIds,
      includesAnalysis: [CallIntelTypes.IncludesAnalysis.CALL_SENTIMENT],
      // Added opportunities true to match with the other call intel widget and prevent extra API calls
      statsIncludes: { opportunities: true, serviceQuality: false },
      comparisonPeriod: 'week',
      fetchOverviewDetails: true,
    });

  const totalCallsAnalyzed = currentStats?.totalCallsAnalyzed ?? 0;
  const previousWeekTotalCallsAnalyzed = previousStats?.totalCallsAnalyzed ?? 0;

  const { percent, percentChange, unhappyCallCount } = useMemo(() => {
    if (currentOverviewDetails && previousOverviewDetails) {
      const unhappyCallCount =
        currentOverviewDetails.sentiments?.[CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE] ?? 0;
      const percent = totalCallsAnalyzed ? Math.round((unhappyCallCount / totalCallsAnalyzed) * 100) : 0;

      const previousWeekUnhappyCallCount =
        previousOverviewDetails.sentiments?.[CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE] ?? 0;

      const previousWeekPercent = previousWeekTotalCallsAnalyzed
        ? Math.round((previousWeekUnhappyCallCount / previousWeekTotalCallsAnalyzed) * 100)
        : 0;

      const percentChange = percent - previousWeekPercent;

      return {
        unhappyCallCount,
        percent,
        percentChange,
      };
    }
    return {
      percent: 0,
      percentChange: 0,
      unhappyCallCount: 0,
    };
  }, [currentOverviewDetails, previousOverviewDetails, totalCallsAnalyzed, previousWeekTotalCallsAnalyzed]);

  return (
    <div css={styles.summaryPanel}>
      <Text as='span' weight='bold' size='large' css={styles.countHeader}>
        {unhappyCallCount}
      </Text>
      <div css={styles.content}>
        <UnhappyCallsBadge />
        <div css={{ display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'flex-end' }}>
          <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            <Text color='subdued' as='span' weight='bold' css={{ fontSize: theme.font.size.h2 }}>
              {percent}%
            </Text>
            {!!unhappyCallCount && !!totalCallsAnalyzed && (
              <PercentChangeIndicator
                percentChange={percentChange}
                comparisonPeriod='week'
                percentChangeDisplayMap={{
                  increase: { iconName: 'up-small', iconColor: 'error', fontColor: 'error' },
                  decrease: { iconName: 'down-small', iconColor: 'success', fontColor: 'success' },
                  neutral: { iconName: 'up-small', iconColor: 'subdued', fontColor: 'subdued' },
                }}
              />
            )}
          </div>
          <Text as='span' color='subdued' size='medium'>
            {t('of {{count}} Calls Analyzed', { count: totalCallsAnalyzed || 0 })}
          </Text>
        </div>
      </div>
    </div>
  );
};

const UnhappyCallsBadge = () => {
  const { t } = useTranslation('callIntelDashboardWidgets');

  return (
    <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
      <CallIntelChip
        dataKey={CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE}
        label={''}
        chipVariants={{
          [CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE]: {
            variant: 'critical',
            leftIconName: 'emoji-sad-small',
          },
        }}
      />
      <Text color='subdued' size='medium'>
        {t('Unhappy Calls')}
      </Text>
    </div>
  );
};

const styles = {
  summaryPanel: css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  `,
  countHeader: css`
    font-size: ${theme.fontSize(138)};
    line-height: 1;
    @container (max-width: ${breakpoints.small.min}px) {
      font-size: ${theme.fontSize(128)};
    }
  `,

  content: css`
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: flex-end;
    gap: ${theme.spacing(2)};
    @container (max-width: ${breakpoints.small.min}px) {
      flex-direction: row;
      justify-content: flex-start;
      align-items: flex-end;
    }
  `,
};
